import styled from 'styled-components';

import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import { FieldContainer } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import { applyColorFromProps } from 'packages/kakapo-ui-kit/helpers/Theme';

export enum ToggleSize {
	Small = 'Small',
	Normal = 'Normal',
	Large = 'Large',
}

interface IToggleInnerProps {
	width: number;
	height: number;
}

interface IToggleSliderProps {
	checked: boolean;
	width: number;
	height: number;
}

interface IToggleInputCheckboxProps {
	translate: any;
}

interface IToggleLabelProps {
	fontSize: number;
}

const ToggleContainer = styled.label`
	display: flex;
	justify-content: left;
	align-items: center;
`;

const ToggleInner = styled.label<IToggleInnerProps>`
	position: relative;
	display: inline-block;
	width: ${({ width }) => width}px;
	height: ${({ height }) => height}px;
	> input {
		display: none;
	}
`;

const ToggleInputCheckbox = styled.input<IToggleInputCheckboxProps>`
	&:checked + span {
		background-color: rgba(${(props) => toRgbString(props.theme.colors.green)}, 0.3);
	}
	&:disabled + span {
		background-color: ${(props) => props.theme.colors.whiteBlue};
		opacity: 0.4;
		cursor: not-allowed;
	}
	&:disabled:checked + span {
		background-color: rgba(${(props) => toRgbString(props.theme.colors.green)}, 0.3);
		opacity: 0.4;
		cursor: not-allowed;
	}
	&:focus + span {
		box-shadow: 0 0 1px #2196f3;
	}
	&:checked + span:before {
		-webkit-transform: translateX(${({ translate }) => translate}px);
		-ms-transform: translateX(${({ translate }) => translate}px);
		transform: translateX(${({ translate }) => translate}px);
	}
`;

const ToggleSlider = styled.span<IToggleSliderProps>`
	position: absolute;
	cursor: pointer;
	display: flex;
	align-items: center;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${(props) => props.theme.colors.whiteBlue};
	-webkit-transition: 0.2s;
	transition: 0.2s;
	border-radius: 34px;
	&:before {
		position: relative;
		border-radius: 50%;
		content: '';
		height: ${({ height: sliderHeight }) => sliderHeight}px;
		width: ${({ width: sliderWidth }) => sliderWidth}px;
		left: 4px;
		background-color: ${({ checked, theme }) =>
			!checked ? theme.colors.lightBlueDark : theme.colors.green};
		-webkit-transition: 0.2s;
		transition: 0.2s;
	}
`;

const ToggleLabel = styled.span<IToggleLabelProps>`
	padding: 0 10px;
	font-size: ${({ fontSize }) => fontSize}px;
	color: ${(props) => applyColorFromProps(props, 'slateBlue')};
`;

interface IToggleProps {
	checked: boolean;
	disabled?: boolean;
	onChange: () => void;
	name: string;
	value: string;
	labelRight?: string;
	labelLeft?: string;
	size?: ToggleSize;
	unspaced?: boolean;
}

const sizes = {
	[ToggleSize.Small]: {
		sliderWidth: 20,
		sliderHeight: 20,
		toggleWidth: 50,
		toggleHeight: 28,
		translate: 22,
		fontSize: 10,
	},
	[ToggleSize.Normal]: {
		sliderWidth: 26,
		sliderHeight: 26,
		toggleWidth: 60,
		toggleHeight: 34,
		translate: 26,
		fontSize: 14,
	},
	[ToggleSize.Large]: {
		sliderWidth: 32,
		sliderHeight: 32,
		toggleWidth: 68,
		toggleHeight: 42,
		translate: 28,
		fontSize: 16,
	},
};

const Toggle = ({
	onChange,
	checked,
	disabled,
	name,
	value,
	labelRight,
	labelLeft,
	size,
	unspaced = false,
}: IToggleProps) => {
	const { sliderWidth, sliderHeight, toggleWidth, toggleHeight, translate, fontSize } = sizes[
		size ?? ToggleSize.Normal
	];

	return (
		<FieldContainer unspaced={unspaced}>
			<ToggleContainer>
				{labelLeft && <ToggleLabel fontSize={fontSize}>{labelLeft}</ToggleLabel>}
				<ToggleInner onChange={onChange} width={toggleWidth} height={toggleHeight}>
					<ToggleInputCheckbox
						type="checkbox"
						name={name}
						value={value}
						checked={checked}
						disabled={disabled}
						translate={translate}
					/>
					<ToggleSlider checked={checked} width={sliderWidth} height={sliderHeight} />
				</ToggleInner>
				{labelRight && <ToggleLabel fontSize={fontSize}>{labelRight}</ToggleLabel>}
			</ToggleContainer>
		</FieldContainer>
	);
};

export default Toggle;
